import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Search, Cpu, Wifi, User, Clock, ShieldAlert } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const fechaHora = (d) => (d ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const diasDesde = (d) => (d ? Math.floor((Date.now() - new Date(d)) / 86400000) : null);

/**
 * Software y seguridad de un equipo, tomados de Vicarius (se actualiza solo cada noche; botón para actualizar ahora).
 */
export default function VicariusActivoPanel({ activoId }) {
    const { user } = useAuth();
    const puedeSincronizar = ['admin', 'soporte'].includes(user?.role?.toLowerCase());
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);
    const [error, setError] = useState('');
    const [filtro, setFiltro] = useState('');

    const cargar = useCallback(async () => {
        try {
            const r = await api.get(`/vicarius/activo/${activoId}`);
            setDatos(r.data);
            setError('');
        } catch (e) {
            setError(e.response?.data?.message || 'No se pudo cargar la información de Vicarius.');
        } finally {
            setCargando(false);
        }
    }, [activoId]);

    useEffect(() => { setCargando(true); cargar(); }, [cargar]);

    const sincronizar = async () => {
        setSincronizando(true);
        setError('');
        try {
            const r = await api.post(`/vicarius/activo/${activoId}/sincronizar`);
            setDatos(r.data);
        } catch (e) {
            setError(e.response?.data?.message || 'No se pudo sincronizar con Vicarius.');
        } finally {
            setSincronizando(false);
        }
    };

    const software = useMemo(() => {
        const q = filtro.trim().toLowerCase();
        const lista = datos?.software || [];
        return q ? lista.filter((s) => `${s.nombre} ${s.fabricante || ''}`.toLowerCase().includes(q)) : lista;
    }, [datos, filtro]);

    if (cargando) return <div className="p-6 text-sm text-gray-500">Cargando información de Vicarius…</div>;

    const v = datos?.vicarius;
    const dias = diasDesde(v?.ultimoReporte);

    return (
        <div className="space-y-6 mb-6">
            {/* Estado del equipo en Vicarius */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Equipo en Vicarius</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {v ? `Actualizado ${fechaHora(v.sincronizado)} · se renueva solo cada noche` : 'Este equipo todavía no figura sincronizado con Vicarius'}
                        </p>
                    </div>
                    {puedeSincronizar && (
                        <button type="button" onClick={sincronizar} disabled={sincronizando}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                            <RefreshCw size={14} className={sincronizando ? 'animate-spin' : ''} /> {sincronizando ? 'Actualizando…' : 'Actualizar ahora'}
                        </button>
                    )}
                </div>
                {error && <p className="px-5 py-3 text-sm text-red-600">{error}</p>}
                {v ? (
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 p-5 text-sm">
                        {[
                            [Cpu, 'Sistema operativo', [v.sistemaOperativo, v.arquitectura].filter(Boolean).join(' · ') || '—'],
                            [Clock, 'Último reporte', `${fechaHora(v.ultimoReporte)}${dias != null && dias > 7 ? ` (hace ${dias} d)` : ''}`],
                            [Wifi, 'IP y dominio', [v.ip, v.dominio].filter(Boolean).join(' · ') || '—'],
                            [User, 'Último usuario', v.usuarioActual || '—'],
                            [ShieldAlert, 'Riesgo', v.riesgoNivel ? `${v.riesgoNivel}${v.riesgoPuntaje != null ? ` (${v.riesgoPuntaje}/10)` : ''}` : '—'],
                            [ShieldAlert, 'Vulnerabilidades', v.vulnerabilidades != null ? String(v.vulnerabilidades) : '—'],
                            [Cpu, 'Agente de Vicarius', v.agenteVersion ? `v${v.agenteVersion}` : '—'],
                            [Wifi, 'Conexión', v.alive ? 'En línea ahora' : 'Sin conexión ahora'],
                        ].map(([Icono, etiqueta, valor]) => (
                            <div key={etiqueta}>
                                <dt className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"><Icono size={13} /> {etiqueta}</dt>
                                <dd className="mt-0.5 font-medium text-gray-900 dark:text-white break-words">{valor}</dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <p className="p-5 text-sm text-gray-500 dark:text-gray-400">
                        Vicarius identifica los equipos por su nombre. Si este equipo tiene el agente instalado con otro nombre, no se podrá relacionar automáticamente.
                    </p>
                )}
            </div>

            {/* Programas de seguridad */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Programas de seguridad</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Se detectan solos a partir del software instalado.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
                    {(datos?.seguridad || []).map((p) => (
                        <div key={p.id} className={`rounded-lg border p-3 ${p.detectado ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-800' : p.requerido ? 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800' : 'border-gray-200 dark:border-gray-700'}`}>
                            <div className="flex items-center gap-2">
                                {p.detectado ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" /> : <XCircle size={18} className="text-red-500 shrink-0" />}
                                <span className="font-semibold text-gray-900 dark:text-white">{p.nombre}</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{p.detectado ? p.evidencia : (p.requerido ? 'No detectado — requerido' : 'No detectado')}</p>
                            <p className="text-[11px] text-gray-400">{p.tipo}</p>
                        </div>
                    ))}
                    {(!datos?.seguridad || datos.seguridad.length === 0) && <p className="text-sm text-gray-500">No hay programas de seguridad configurados.</p>}
                </div>
            </div>

            {/* Software instalado */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Software instalado</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{(datos?.software || []).length} aplicaciones detectadas por Vicarius</p>
                    </div>
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar aplicación o fabricante…"
                            className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white w-64 max-w-full" />
                    </div>
                </div>
                {software.length === 0 ? (
                    <p className="p-5 text-sm text-gray-500 dark:text-gray-400">{(datos?.software || []).length === 0 ? 'Aún no hay software detectado para este equipo.' : 'Sin resultados.'}</p>
                ) : (
                    <div className="max-h-[28rem] overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 text-[11px] uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left px-5 py-2 font-semibold">Aplicación</th>
                                    <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Fabricante</th>
                                    <th className="text-left px-3 py-2 font-semibold">Versión</th>
                                    <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Detectado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {software.map((s, i) => (
                                    <tr key={`${s.nombre}-${s.version}-${i}`}>
                                        <td className="px-5 py-2 text-gray-900 dark:text-white">{s.nombre}</td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300 hidden sm:table-cell">{s.fabricante || '—'}</td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{s.version || '—'}</td>
                                        <td className="px-3 py-2 text-gray-400 text-xs hidden md:table-cell">{fechaHora(s.primeraVez)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
